import {MutableRefObject, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router";
import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {Hidden} from "@pg-design/grid-module";
import {FiltersIcon} from "@pg-design/icons-module";
import {appendQueryString} from "@pg-mono/request";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {HOTLINE_INFO_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {NewsletterFlowWithRegistrationTrigger} from "../../newsletter/components/NewsletterFlowWithRegistrationTrigger";
import {useSubscribeToNewsletter} from "../../newsletter/hooks/useSubscribeToNewsletter";
import {NotificationVariant} from "../../newsletter/types/NotificationVariant";
import {getOfferListSubFilterParsed} from "../../offer/helpers/friendly_offer_list/get_offer_list_sub_filter_parsed";
import {useOffersViewsUrl} from "../../offer/hooks/use_offers_views_url";
import {resetPrivilegedOfferList as resetPrivilegedOfferListAction} from "../../offer/list/actions/fetch_privileged_offer_list";
import {OfferListSortMobile} from "../../offer/list/components/OfferListsSortMobile";
import {SearchSortString} from "../../offer/list/constants/SearchSort";
import {useDefaultDistance} from "../../offer/list/hooks/use_default_distance";
import {useOfferListingAbroadView} from "../../offer/list/hooks/use_offer_listing_abroad_view_check";
import {trackAmplitudeListingFiltersApplied} from "../../offer/tracking/amplitude/track_amplitude_listing_filters_applied";
import {propertyConfiguratorSearchFormFields} from "../../property_configurator/reducers/property_configurator_reducer";
import {hitFiltersInteraction} from "../../real_estate/tracking/hit_filters_interaction";
import {convertSearchFormToFiltersTracking} from "../../real_estate/utils/convert_search_form_to_filters_tracking";
import {SemanticSearchConversation} from "../../semantic_search/components/semantic_search_conversation/SemanticSearchConversation";
import {SemanticSearchQuery} from "../../semantic_search/components/semantic_search_query/SemanticSearchQuery";
import {SemanticSearchSidebarHeader} from "../../semantic_search/components/semantic_search_sidebar/SemanticSearchSidebarHeader";
import {SemanticSearchBetaBadge} from "../../semantic_search/components/SemanticSearchBetaBadge";
import {SemanticSearchClientIcon} from "../../semantic_search/components/SemanticSearchClientIcon";
import {selectSemanticSearchHasConversation} from "../../semantic_search/state/select_semantic_search_has_conversation";
import {hitAiAssistant} from "../../semantic_search/tracking/semantic_search_gtm_events";
import {useAppDispatch} from "../../store/hooks";
import {debouncedSearchTypingHit} from "../../tracking/algolytics_hits/search_typing_hit";
import {ILatestQuery} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {fetchAllSearchLists as fetchAllSearchListsAction, IFetchAllSearchListsParams} from "../actions/fetch_search_all_action";
import {resetPlaceList as resetPlaceListAction} from "../actions/fetch_search_places_action";
import {
    resetMultiRegionSearchForm as resetMultiRegionSearchFormAction,
    resetSearchForm as resetSearchFormAction,
    searchFormActions
} from "../actions/search_form_actions";
import {resetActiveDropdownItem as resetActiveDropdownItemAction} from "../actions/set_active_dropdown_item";
import {setPreviousDistance as setPreviousDistanceAction} from "../actions/set_travel_time";
import {searchFormFields} from "../constants/search_form_constants";
import {SEARCH_FORM_FOCUS_HASH} from "../constants/search_form_focus_hash";
import {searchFormWithDrawerHeight} from "../constants/search_form_with_drawer_height";
import {useCountSearchFilters} from "../hooks/use_count_search_filters";
import {useFilter} from "../hooks/use_filter";
import {useMobileFilters} from "../hooks/use_mobile_filters";
import {useRegionSearchTabs} from "../hooks/use_region_search_tabs";
import useSearchAndFilterUpdate from "../hooks/use_search_and_filter_update";
import {useSearchForm} from "../hooks/use_search_form";
import {useSearchFormSubmit} from "../hooks/use_search_form_submit";
import {filtersModalOpened} from "../slices/filters_visibility_slice";
import {useTrackAmplitudeGlobalSearch} from "../tracking/use_track_amplitude_global_search";
import {ISearchFiltersFormValues} from "../types/ISearchFiltersFormValues";
import {RegionSearchTabsType} from "../types/RegionSearchTabsType";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {SearchTab} from "../types/SearchTab";
import {getSearchFiltersByOfferType} from "../utils/get_search_filters_by_offer_type";
import {googlePlacesDivId} from "../utils/search_constants";
import {ISearchAutocompleteFormValues} from "./search_autocomplete/SearchAutocomplete";
import {SlotOrderType} from "./search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchAutocompleteWithDropdown} from "./search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchAutocompleteWithRegionAdjustments} from "./search_autocomplete/SearchAutocompleteWithRegionAdjustments";
import {HotlineInfo} from "./HotlineInfo";
import {MobileSearchTriggers} from "./MobileSearchTriggers";
import {MobileTopSearchTriggers} from "./MobileTopSearchTriggers";
import {SearchFilters} from "./SearchFilters";

interface IProps {
    latestQuery: ILatestQuery;
    hasParentBeenInitialized?: boolean;
    multiRegionLink: () => string;
    listFriendlyLink: (params: {friendlySlug: string; offerListSubFilterSlug?: string}) => string;
    abroadListFriendlyLink: (params: {friendlySlug: string; country: string; abroadRegionSlug?: string}) => string;
    sortActive?: boolean;
    shouldRenderListBackBtn?: boolean;
    blendTop?: string;
    blendHeightDecrease?: string;
    className?: string;
    slotOrder?: SlotOrderType;
    isMap?: boolean;
    showTopFilters?: boolean;
    hideBottomPadding?: boolean;
    disableLocationSearch?: boolean;
    autoFocusOnInit?: boolean;
    disableFilterAutoShowcase?: boolean;
    showMobileTopSearchTrigger?: boolean;
}

export const SearchFormWithDrawer = (props: IProps) => {
    const {
        hasParentBeenInitialized = true,
        disableLocationSearch,
        disableFilterAutoShowcase,
        showMobileTopSearchTrigger,
        sortActive,
        isMap,
        shouldRenderListBackBtn,
        blendTop,
        blendHeightDecrease,
        className,
        latestQuery,
        showTopFilters,
        autoFocusOnInit,
        slotOrder,
        hideBottomPadding,
        multiRegionLink,
        listFriendlyLink,
        abroadListFriendlyLink
    } = props;

    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const [isMobileSemanticSearchModalOpen, setMobileSemanticSearchModalOpen] = useState(false);
    const mobileSemanticSearchScrollRef = useRef<HTMLDivElement>(null);

    const {isLocationSearchOpen: isLocationSearchOpenGlobal, setLocationSearchOpenStatus, toggleLocationSearch} = useSearchForm();
    const {trackAmplitudeSearchAdvanced, firePendingAmplitudeSearch, clearPendingAmplitudeSearch} = useTrackAmplitudeGlobalSearch();
    const formValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;
    const hasConversation = useSelector(selectSemanticSearchHasConversation);
    const dispatch = useAppDispatch();
    const history = useHistory();
    const {saveNewsletterSearchData} = useSubscribeToNewsletter();
    const location = useLocation();

    // Allow manual opening, only prevent auto-opening on initialization
    const isLocationSearchOpen = disableLocationSearch ? false : isLocationSearchOpenGlobal;

    // TODO: setDesktopTypeBlendActive & setDesktopSearchBlendActive need to be refactored,
    // probably display should depend on isLocationSearchOpen and css media query
    const [isDesktopTypeBlendActive, setDesktopTypeBlendActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const searchAutocompleteWrapperRef = useRef<HTMLDivElement>(null);

    const resetSearchForm = (query: Record<string, string>) => dispatch(resetSearchFormAction(query));
    const resetActiveDropdownItem = () => dispatch(resetActiveDropdownItemAction());
    const resetMultiRegionSearchForm = (query: ILatestQuery, locationSearch: string) => dispatch(resetMultiRegionSearchFormAction(query, locationSearch));
    const fetchAllSearchLists = async (searchInput: string, params: IFetchAllSearchListsParams): Promise<SearchTab> =>
        dispatch(fetchAllSearchListsAction(searchInput, params));
    const resetPrivilegedOfferList = () => dispatch(resetPrivilegedOfferListAction());
    const setPreviousDistance = (distance: string | number | undefined) => setPreviousDistanceAction(distance)(dispatch);
    const resetPlaceList = () => dispatch(resetPlaceListAction());

    const formActions = {
        update: (values: Partial<Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>>) =>
            dispatch(searchFormActions.update(values)),
        replace: (values: Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>) =>
            dispatch(searchFormActions.replace(values)),
        clear: () => dispatch(searchFormActions.clear())
    };

    // Narrowed formActions for components that only need searchFormFields
    const searchFormActionsOnly = {
        update: (values: Partial<SearchFormValuesType>) =>
            formActions.update(values as Partial<Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>>),
        replace: (values: SearchFormValuesType) =>
            formActions.replace(values as Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>),
        clear: () => formActions.clear()
    };

    const {activeRegionSearchTab, regionSearchTabs, regionSearchTabChangeHandler} = useRegionSearchTabs({
        regionQuery: formValues?.search?.label,
        onSearchTabChange: (tab: RegionSearchTabsType) => {
            if (tab === RegionSearchTabsType.TRAVEL_TIME) {
                setPreviousDistance(latestQuery.distance);
            }
        }
    });

    const scrollIntoSearchAutocompleteWrapper = () => {
        if (searchAutocompleteWrapperRef.current) {
            const marginOffset = 70;
            const elementTop = searchAutocompleteWrapperRef.current.getBoundingClientRect().top;
            const offsetTop = document.documentElement.scrollTop;
            const scrollToPosition = elementTop + offsetTop - marginOffset;

            window.scrollTo({
                top: scrollToPosition,
                behavior: "smooth"
            });
        }
    };

    const {isMobileFiltersActive, closeMobileFilterModal, toggleMobileFilterModal, prevFilterState} = useMobileFilters({
        formValues,
        delayAutoOpen: true,
        onClose: (makeGtmHit?: boolean) => {
            makeGtmHit && hitFiltersInteraction("close", convertSearchFormToFiltersTracking(formValues));
        }
    });
    const isAbroadOffersView = useOfferListingAbroadView();

    const [windowHeight, setWindowHeight] = useState(0);
    const {isMobile} = useUserDevice();

    useEffect(() => {
        if (!isMobile) {
            setDesktopSearchBlendActive(isLocationSearchOpen);

            if (autoFocusOnInit && isLocationSearchOpen) {
                scrollIntoSearchAutocompleteWrapper();
            }
        }
    }, [isMobile, isLocationSearchOpen]);

    // handle initial focus on search
    const focusOnSearch = (ref: MutableRefObject<HTMLElement | null>, callback?: () => void) => {
        if (location.hash === SEARCH_FORM_FOCUS_HASH && ref.current?.offsetParent) {
            history.replace({hash: ""});
            setLocationSearchOpenStatus(true);
            callback && callback();
        }
    };
    // focus on search
    const onInputMount = (ref: MutableRefObject<HTMLInputElement | null>) => {
        focusOnSearch(ref, () =>
            setTimeout(() => {
                ref.current?.focus();
            }, 0)
        );
    };
    // and mobile focus on search
    const onMobileSearchTriggerMount = (ref: MutableRefObject<HTMLDivElement | null>) => {
        focusOnSearch(ref);
    };

    useEffect(() => {
        // TODO: This condition is to maintain compatibility with old forms and allow building latestQuery during component's lifecycle.
        // In the future it would be better to rewrite whole form defaults logic.
        if (!hasParentBeenInitialized) {
            return;
        }

        const query = latestQuery as Record<string, string>;

        (async () => {
            if (location.pathname === multiRegionLink()) {
                resetMultiRegionSearchForm(query, location.search);
            } else {
                resetSearchForm(query);
            }
        })();
    }, [hasParentBeenInitialized]);

    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isDesktopSearchBlendActive]);

    /**
     * Callbacks
     */
    const onAfterTabSwitch = () => {
        resetActiveDropdownItem();
    };

    /**
     * Submit logic
     */
    const onSubmit = useSearchFormSubmit({
        latestQuery,
        listFriendlyLink: ({friendlySlug, offerListSubFilterSlug}) => {
            if (viewType.current === ViewType.OFFER_LIST_MAP && offerListSubFilterSlug) {
                const subFilterQuery = getOfferListSubFilterParsed(offerListSubFilterSlug);
                return appendQueryString(
                    listFriendlyLink({
                        friendlySlug,
                        offerListSubFilterSlug
                    }),
                    subFilterQuery
                );
            }

            return listFriendlyLink({friendlySlug, offerListSubFilterSlug});
        },
        abroadListFriendlyLink: (params) => {
            return abroadListFriendlyLink && abroadListFriendlyLink(params);
        },
        multiRegionLink,
        onSearchSubmit: (formValues) => {
            if (formValues.search?.regions) {
                saveNewsletterSearchData(formValues);
            }
        }
    });

    const onChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        if (!!values.search?.label && formValues.search.label !== values.search.label) {
            debouncedSearchTypingHit({query: values.search.label});
            // Discard a popular-city pending so a subsequent submit doesn't fire popular_search_click.
            clearPendingAmplitudeSearch();
        }

        formActions.update(values);
    };
    const submitWithPending = (fv: SearchFormValuesType, userQueryParams?: {distance?: number}) => {
        // Fire global_search staged by a popular city click before navigating; no-op when
        // the submit was triggered by a filter change or a manually typed region.
        firePendingAmplitudeSearch();
        onSubmit(fv, userQueryParams);
    };

    const onSearchSubmit = (userQueryParams?: {distance?: number}) => {
        // Fire global_search first so _lastSearchMethod is set before trackAmplitudeSearchAdvanced reads it.
        firePendingAmplitudeSearch();
        trackAmplitudeSearchAdvanced(userQueryParams);
        onSubmit(formValues, userQueryParams);
    };

    const onSearchFiltersSubmit = () => {
        trackAmplitudeListingFiltersApplied({formValues: formValues as ISearchFiltersFormValues, latestQuery});
        onSubmit(formValues);
    };

    const {onAfterChange} = useSearchAndFilterUpdate({
        resetPrivilegedOfferList,
        formValues,
        latestQuery,
        onSubmit: submitWithPending
    });

    const {isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters} = useFilter({
        formValues,
        formActions: searchFormActionsOnly,
        closeMobileFilterModal: closeMobileFilterModal,
        onAfterChange: onAfterChange,
        onSubmit: (formValues: SearchFormValuesType, userQueryParams?: {distance?: number}) => onSubmit(formValues, userQueryParams),
        enableFiltersAutoOpen: false,
        disableAutoShowcase: disableFilterAutoShowcase
    });

    const filtersButtonCLick = () => {
        dispatch(filtersModalOpened({filtersModalOpened: true}));
        setDrawerOpen(true);
    };

    /**
     * Handle filters
     */
    const searchFiltersConfig = getSearchFiltersByOfferType(formValues.type?.[0]);
    const {totalCount: numberOfSearchFilters} = useCountSearchFilters(formValues as SearchFormValuesType, searchFiltersConfig);

    const {listUrl, mapUrl} = useOffersViewsUrl();

    const onTravelTimeCancel = () => {
        regionSearchTabChangeHandler(RegionSearchTabsType.LOCATION);
        dispatch(resetPlaceList());
    };

    const renderSearchAutocomplete = (onDropdownStatusChange: (isOpen: boolean) => void, isInModal?: boolean) => {
        if (disableLocationSearch) {
            return null;
        }

        return isAbroadOffersView ? (
            <SearchAutocompleteWithDropdown
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={setLocationSearchOpenStatus}
                formProps={{
                    formValues,
                    formActions,
                    fetchAllSearchLists,
                    latestQuery
                }}
                onAfterChange={onAfterChange}
                onSearchSubmit={onSearchSubmit}
                onAfterTabSwitch={() => null}
                onInputMount={() => null}
                includeDistanceFilter
                isInModal={isInModal}
                placeholder="Zmień lokalizację"
                showArrowOnMobile
                blockBodyScroll
            />
        ) : (
            <SearchAutocompleteWithRegionAdjustments
                ref={searchAutocompleteWrapperRef}
                activeRegionSearchTab={activeRegionSearchTab}
                defaultDistanceValue={defaultDistanceValue}
                fetchAllSearchLists={fetchAllSearchLists}
                formActions={searchFormActionsOnly}
                formValues={formValues as SearchFormValuesType}
                isInModal={isInModal}
                onAfterChange={onAfterChange}
                onAfterTabSwitch={onAfterTabSwitch}
                onChange={onChange}
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={onDropdownStatusChange}
                onInputMount={onInputMount}
                onSearchSubmit={onSearchSubmit}
                onTravelTimeCancel={onTravelTimeCancel}
                setDesktopTypeBlendActive={setDesktopTypeBlendActive}
                slotOrder={slotOrder}
                regionSearchTabChangeHandler={regionSearchTabChangeHandler}
                regionSearchTabs={regionSearchTabs}
                blockBodyScroll
            />
        );
    };

    /**
     * Render
     */
    const defaultDistanceValue = useDefaultDistance(formValues.distance || latestQuery?.distance);
    const mobileFiltersProps = {
        selectedRegionsCount: formValues["search"].regions ? formValues["search"].regions.length : 0,
        selectedFiltersCount: numberOfSearchFilters,
        onToggleSearchClick: toggleLocationSearch,
        onToggleFiltersClick: toggleMobileFilterModal,
        onToggleSortClick: sortActive ? () => setMobileSortActive((prev) => !prev) : null,
        onSemanticSearchClick: () => {
            hitAiAssistant("open");
            setMobileSemanticSearchModalOpen(true);
        },
        mapUrl: shouldRenderListBackBtn ? mapUrl : undefined,
        listUrl: !shouldRenderListBackBtn ? listUrl : undefined,
        latestQuery,
        onMount: onMobileSearchTriggerMount,
        firstSelectedRegion: formValues.search?.regions?.[0]?.name,
        sortActive
    };

    return (
        <div className={cx(wrapperStyle, className)}>
            <DesktopBlend
                isActive={isDesktopSearchBlendActive || isDesktopTypeBlendActive}
                pageHeight={`${windowHeight}px`}
                top={blendTop || `${searchFormWithDrawerHeight}px`}
                heightDecrease={blendHeightDecrease ? blendHeightDecrease : `${searchFormWithDrawerHeight}px`}
            />

            <Hidden on="desktop" className={css({py: 1.5})}>
                <HotlineInfo hotlinePhoneNumber={HOTLINE_INFO_PHONE_NUMBER} />
            </Hidden>

            {!isMobile && (
                <div className={searchSectionWrapper}>
                    <Hidden on="mobile">
                        <HotlineInfo hotlinePhoneNumber={HOTLINE_INFO_PHONE_NUMBER} />
                    </Hidden>

                    <div className={searchFormWrapperStyle}>
                        <div className={searchInputWrapper}>
                            <div id={googlePlacesDivId} />
                            {renderSearchAutocomplete(setLocationSearchOpenStatus)}
                        </div>
                    </div>
                    <div className={filtersButtonsStyle}>
                        <Button size="small" variant="filled_primary" iconLeft={FiltersIcon} className={actionButtonStyle} onClick={filtersButtonCLick}>
                            FILTRY
                            {numberOfSearchFilters > 0 ? (
                                <Badge variant="label_danger" className={filtersCountBadge}>
                                    {numberOfSearchFilters}
                                </Badge>
                            ) : null}
                        </Button>
                        {formValues.search?.regions && (
                            <NewsletterFlowWithRegistrationTrigger variant={NotificationVariant.ICON} amplitudePlacement="rp_save_alert_search_drawer" />
                        )}
                    </div>
                </div>
            )}

            <SearchFilters
                useFilterHook={{isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters}}
                useMobileFilterHook={{isMobileFiltersActive, closeMobileFilterModal, prevFilterState, toggleMobileFilterModal}}
                latestQuery={latestQuery}
                handleSubmit={onSearchSubmit}
                values={formValues as SearchFormValuesType}
                onChange={formActions.update}
                onAfterChange={onAfterChange}
                onSubmit={onSearchFiltersSubmit}
                isAbroad={isAbroadOffersView}
            />

            {isMobile && (
                <>
                    {showTopFilters ? (
                        <MobileTopSearchTriggers
                            forceShowTopSearchTrigger={showMobileTopSearchTrigger}
                            searchFieldPropsValue={formValues.search}
                            {...mobileFiltersProps}
                            isMap={isMap}
                            showLocationSearch
                        />
                    ) : (
                        <MobileSearchTriggers {...mobileFiltersProps} positionFixed hideBottomPadding={hideBottomPadding} />
                    )}
                    {sortActive && (
                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={() => setMobileSortActive(false)}>
                            <OfferListSortMobile
                                name="mobile-sort"
                                onSubmit={() => setMobileSortActive(false)}
                                latestQuerySort={latestQuery.sort as SearchSortString}
                                onMobileResetForm={resetFilters}
                            />
                        </MobileOptionsModal>
                    )}

                    <MobileOptionsModal
                        modalHeading={
                            <span className={css({display: "inline-flex", alignItems: "center", gap: 1})}>
                                Asystent AI
                                <SemanticSearchBetaBadge />
                            </span>
                        }
                        isOpen={isMobileSemanticSearchModalOpen}
                        onModalClose={() => {
                            hitAiAssistant("close");
                            setMobileSemanticSearchModalOpen(false);
                        }}
                        className={css({justifyContent: !hasConversation ? "center" : "initial"})}
                        scrollContainerRef={mobileSemanticSearchScrollRef}
                    >
                        {!hasConversation && <SemanticSearchClientIcon />}
                        <SemanticSearchSidebarHeader className={sidebarHeader} />
                        <SemanticSearchConversation containerRef={mobileSemanticSearchScrollRef} />
                        <SemanticSearchQuery hasStickySidebar />
                    </MobileOptionsModal>

                    <MobileOptionsModal
                        modalHeading="Wyszukiwanie"
                        isOpen={isLocationSearchOpen}
                        onModalClose={() => {
                            setLocationSearchOpenStatus(false);
                        }}
                    >
                        <div id={googlePlacesDivId} />
                        {renderSearchAutocomplete(setLocationSearchOpenStatus, true)}
                    </MobileOptionsModal>
                </>
            )}
        </div>
    );
};

const searchSectionWrapper = css({
    display: "none",

    md: {
        mx: "auto",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        pt: 2,
        pb: 1,
        gap: 2,
        position: "relative",
        flexGrow: 1,
        flexShrink: 1,
        flexWrap: "wrap"
    }
});

const searchFormWrapperStyle = css({
    display: "flex",
    flex: "1 1"
});

const searchInputWrapper = css({
    display: "none",

    md: {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        flex: "1 1"
    }
});

const actionButtonStyle = css({
    width: "18rem",
    overflow: "visible",
    display: "none",

    md: {
        display: "inline-flex"
    }
});

const filtersCountBadge = css({
    position: "absolute",
    right: "-0.5rem",
    top: "-0.5rem"
});

const wrapperStyle = css({
    "&.isInNavigationBar": {
        pb: 0,
        "& > div:nth-of-type(2)": {
            pt: 0
        }
    }
});

const filtersButtonsStyle = css({
    display: "flex",
    gap: 2
});

const sidebarHeader = css({
    p: 1.5
});
import {MutableRefObject, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router";
import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {Hidden} from "@pg-design/grid-module";
import {FiltersIcon} from "@pg-design/icons-module";
import {appendQueryString} from "@pg-mono/request";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {HOTLINE_INFO_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {NewsletterFlowWithRegistrationTrigger} from "../../newsletter/components/NewsletterFlowWithRegistrationTrigger";
import {useSubscribeToNewsletter} from "../../newsletter/hooks/useSubscribeToNewsletter";
import {NotificationVariant} from "../../newsletter/types/NotificationVariant";
import {getOfferListSubFilterParsed} from "../../offer/helpers/friendly_offer_list/get_offer_list_sub_filter_parsed";
import {useOffersViewsUrl} from "../../offer/hooks/use_offers_views_url";
import {resetPrivilegedOfferList as resetPrivilegedOfferListAction} from "../../offer/list/actions/fetch_privileged_offer_list";
import {OfferListSortMobile} from "../../offer/list/components/OfferListsSortMobile";
import {SearchSortString} from "../../offer/list/constants/SearchSort";
import {useDefaultDistance} from "../../offer/list/hooks/use_default_distance";
import {useOfferListingAbroadView} from "../../offer/list/hooks/use_offer_listing_abroad_view_check";
import {trackAmplitudeListingFiltersApplied} from "../../offer/tracking/amplitude/track_amplitude_listing_filters_applied";
import {propertyConfiguratorSearchFormFields} from "../../property_configurator/reducers/property_configurator_reducer";
import {hitFiltersInteraction} from "../../real_estate/tracking/hit_filters_interaction";
import {convertSearchFormToFiltersTracking} from "../../real_estate/utils/convert_search_form_to_filters_tracking";
import {SemanticSearchConversation} from "../../semantic_search/components/semantic_search_conversation/SemanticSearchConversation";
import {SemanticSearchQuery} from "../../semantic_search/components/semantic_search_query/SemanticSearchQuery";
import {SemanticSearchSidebarHeader} from "../../semantic_search/components/semantic_search_sidebar/SemanticSearchSidebarHeader";
import {SemanticSearchBetaBadge} from "../../semantic_search/components/SemanticSearchBetaBadge";
import {SemanticSearchClientIcon} from "../../semantic_search/components/SemanticSearchClientIcon";
import {selectSemanticSearchHasConversation} from "../../semantic_search/state/select_semantic_search_has_conversation";
import {hitAiAssistant} from "../../semantic_search/tracking/semantic_search_gtm_events";
import {useAppDispatch} from "../../store/hooks";
import {debouncedSearchTypingHit} from "../../tracking/algolytics_hits/search_typing_hit";
import {ILatestQuery} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {fetchAllSearchLists as fetchAllSearchListsAction, IFetchAllSearchListsParams} from "../actions/fetch_search_all_action";
import {resetPlaceList as resetPlaceListAction} from "../actions/fetch_search_places_action";
import {
    resetMultiRegionSearchForm as resetMultiRegionSearchFormAction,
    resetSearchForm as resetSearchFormAction,
    searchFormActions
} from "../actions/search_form_actions";
import {resetActiveDropdownItem as resetActiveDropdownItemAction} from "../actions/set_active_dropdown_item";
import {setPreviousDistance as setPreviousDistanceAction} from "../actions/set_travel_time";
import {searchFormFields} from "../constants/search_form_constants";
import {SEARCH_FORM_FOCUS_HASH} from "../constants/search_form_focus_hash";
import {searchFormWithDrawerHeight} from "../constants/search_form_with_drawer_height";
import {useCountSearchFilters} from "../hooks/use_count_search_filters";
import {useFilter} from "../hooks/use_filter";
import {useMobileFilters} from "../hooks/use_mobile_filters";
import {useRegionSearchTabs} from "../hooks/use_region_search_tabs";
import useSearchAndFilterUpdate from "../hooks/use_search_and_filter_update";
import {useSearchForm} from "../hooks/use_search_form";
import {useSearchFormSubmit} from "../hooks/use_search_form_submit";
import {filtersModalOpened} from "../slices/filters_visibility_slice";
import {useTrackAmplitudeGlobalSearch} from "../tracking/use_track_amplitude_global_search";
import {ISearchFiltersFormValues} from "../types/ISearchFiltersFormValues";
import {RegionSearchTabsType} from "../types/RegionSearchTabsType";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {SearchTab} from "../types/SearchTab";
import {getSearchFiltersByOfferType} from "../utils/get_search_filters_by_offer_type";
import {googlePlacesDivId} from "../utils/search_constants";
import {ISearchAutocompleteFormValues} from "./search_autocomplete/SearchAutocomplete";
import {SlotOrderType} from "./search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchAutocompleteWithDropdown} from "./search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchAutocompleteWithRegionAdjustments} from "./search_autocomplete/SearchAutocompleteWithRegionAdjustments";
import {HotlineInfo} from "./HotlineInfo";
import {MobileSearchTriggers} from "./MobileSearchTriggers";
import {MobileTopSearchTriggers} from "./MobileTopSearchTriggers";
import {SearchFilters} from "./SearchFilters";

interface IProps {
    latestQuery: ILatestQuery;
    hasParentBeenInitialized?: boolean;
    multiRegionLink: () => string;
    listFriendlyLink: (params: {friendlySlug: string; offerListSubFilterSlug?: string}) => string;
    abroadListFriendlyLink: (params: {friendlySlug: string; country: string; abroadRegionSlug?: string}) => string;
    sortActive?: boolean;
    shouldRenderListBackBtn?: boolean;
    blendTop?: string;
    blendHeightDecrease?: string;
    className?: string;
    slotOrder?: SlotOrderType;
    isMap?: boolean;
    showTopFilters?: boolean;
    hideBottomPadding?: boolean;
    disableLocationSearch?: boolean;
    autoFocusOnInit?: boolean;
    disableFilterAutoShowcase?: boolean;
    showMobileTopSearchTrigger?: boolean;
}

export const SearchFormWithDrawer = (props: IProps) => {
    const {
        hasParentBeenInitialized = true,
        disableLocationSearch,
        disableFilterAutoShowcase,
        showMobileTopSearchTrigger,
        sortActive,
        isMap,
        shouldRenderListBackBtn,
        blendTop,
        blendHeightDecrease,
        className,
        latestQuery,
        showTopFilters,
        autoFocusOnInit,
        slotOrder,
        hideBottomPadding,
        multiRegionLink,
        listFriendlyLink,
        abroadListFriendlyLink
    } = props;

    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const [isMobileSemanticSearchModalOpen, setMobileSemanticSearchModalOpen] = useState(false);
    const mobileSemanticSearchScrollRef = useRef<HTMLDivElement>(null);

    const {isLocationSearchOpen: isLocationSearchOpenGlobal, setLocationSearchOpenStatus, toggleLocationSearch} = useSearchForm();
    const {trackAmplitudeSearchAdvanced, firePendingAmplitudeSearch, clearPendingAmplitudeSearch} = useTrackAmplitudeGlobalSearch();
    const formValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;
    const hasConversation = useSelector(selectSemanticSearchHasConversation);
    const dispatch = useAppDispatch();
    const history = useHistory();
    const {saveNewsletterSearchData} = useSubscribeToNewsletter();
    const location = useLocation();

    // Allow manual opening, only prevent auto-opening on initialization
    const isLocationSearchOpen = disableLocationSearch ? false : isLocationSearchOpenGlobal;

    // TODO: setDesktopTypeBlendActive & setDesktopSearchBlendActive need to be refactored,
    // probably display should depend on isLocationSearchOpen and css media query
    const [isDesktopTypeBlendActive, setDesktopTypeBlendActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const searchAutocompleteWrapperRef = useRef<HTMLDivElement>(null);

    const resetSearchForm = (query: Record<string, string>) => dispatch(resetSearchFormAction(query));
    const resetActiveDropdownItem = () => dispatch(resetActiveDropdownItemAction());
    const resetMultiRegionSearchForm = (query: ILatestQuery, locationSearch: string) => dispatch(resetMultiRegionSearchFormAction(query, locationSearch));
    const fetchAllSearchLists = async (searchInput: string, params: IFetchAllSearchListsParams): Promise<SearchTab> =>
        dispatch(fetchAllSearchListsAction(searchInput, params));
    const resetPrivilegedOfferList = () => dispatch(resetPrivilegedOfferListAction());
    const setPreviousDistance = (distance: string | number | undefined) => setPreviousDistanceAction(distance)(dispatch);
    const resetPlaceList = () => dispatch(resetPlaceListAction());

    const formActions = {
        update: (values: Partial<Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>>) =>
            dispatch(searchFormActions.update(values)),
        replace: (values: Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>) =>
            dispatch(searchFormActions.replace(values)),
        clear: () => dispatch(searchFormActions.clear())
    };

    // Narrowed formActions for components that only need searchFormFields
    const searchFormActionsOnly = {
        update: (values: Partial<SearchFormValuesType>) =>
            formActions.update(values as Partial<Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>>),
        replace: (values: SearchFormValuesType) =>
            formActions.replace(values as Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>),
        clear: () => formActions.clear()
    };

    const {activeRegionSearchTab, regionSearchTabs, regionSearchTabChangeHandler} = useRegionSearchTabs({
        regionQuery: formValues?.search?.label,
        onSearchTabChange: (tab: RegionSearchTabsType) => {
            if (tab === RegionSearchTabsType.TRAVEL_TIME) {
                setPreviousDistance(latestQuery.distance);
            }
        }
    });

    const scrollIntoSearchAutocompleteWrapper = () => {
        if (searchAutocompleteWrapperRef.current) {
            const marginOffset = 70;
            const elementTop = searchAutocompleteWrapperRef.current.getBoundingClientRect().top;
            const offsetTop = document.documentElement.scrollTop;
            const scrollToPosition = elementTop + offsetTop - marginOffset;

            window.scrollTo({
                top: scrollToPosition,
                behavior: "smooth"
            });
        }
    };

    const {isMobileFiltersActive, closeMobileFilterModal, toggleMobileFilterModal, prevFilterState} = useMobileFilters({
        formValues,
        delayAutoOpen: true,
        onClose: (makeGtmHit?: boolean) => {
            makeGtmHit && hitFiltersInteraction("close", convertSearchFormToFiltersTracking(formValues));
        }
    });
    const isAbroadOffersView = useOfferListingAbroadView();

    const [windowHeight, setWindowHeight] = useState(0);
    const {isMobile} = useUserDevice();

    useEffect(() => {
        if (!isMobile) {
            setDesktopSearchBlendActive(isLocationSearchOpen);

            if (autoFocusOnInit && isLocationSearchOpen) {
                scrollIntoSearchAutocompleteWrapper();
            }
        }
    }, [isMobile, isLocationSearchOpen]);

    // handle initial focus on search
    const focusOnSearch = (ref: MutableRefObject<HTMLElement | null>, callback?: () => void) => {
        if (location.hash === SEARCH_FORM_FOCUS_HASH && ref.current?.offsetParent) {
            history.replace({hash: ""});
            setLocationSearchOpenStatus(true);
            callback && callback();
        }
    };
    // focus on search
    const onInputMount = (ref: MutableRefObject<HTMLInputElement | null>) => {
        focusOnSearch(ref, () =>
            setTimeout(() => {
                ref.current?.focus();
            }, 0)
        );
    };
    // and mobile focus on search
    const onMobileSearchTriggerMount = (ref: MutableRefObject<HTMLDivElement | null>) => {
        focusOnSearch(ref);
    };

    useEffect(() => {
        // TODO: This condition is to maintain compatibility with old forms and allow building latestQuery during component's lifecycle.
        // In the future it would be better to rewrite whole form defaults logic.
        if (!hasParentBeenInitialized) {
            return;
        }

        const query = latestQuery as Record<string, string>;

        (async () => {
            if (location.pathname === multiRegionLink()) {
                resetMultiRegionSearchForm(query, location.search);
            } else {
                resetSearchForm(query);
            }
        })();
    }, [hasParentBeenInitialized]);

    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isDesktopSearchBlendActive]);

    /**
     * Callbacks
     */
    const onAfterTabSwitch = () => {
        resetActiveDropdownItem();
    };

    /**
     * Submit logic
     */
    const onSubmit = useSearchFormSubmit({
        latestQuery,
        listFriendlyLink: ({friendlySlug, offerListSubFilterSlug}) => {
            if (viewType.current === ViewType.OFFER_LIST_MAP && offerListSubFilterSlug) {
                const subFilterQuery = getOfferListSubFilterParsed(offerListSubFilterSlug);
                return appendQueryString(
                    listFriendlyLink({
                        friendlySlug,
                        offerListSubFilterSlug
                    }),
                    subFilterQuery
                );
            }

            return listFriendlyLink({friendlySlug, offerListSubFilterSlug});
        },
        abroadListFriendlyLink: (params) => {
            return abroadListFriendlyLink && abroadListFriendlyLink(params);
        },
        multiRegionLink,
        onSearchSubmit: (formValues) => {
            if (formValues.search?.regions) {
                saveNewsletterSearchData(formValues);
            }
        }
    });

    const onChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        if (!!values.search?.label && formValues.search.label !== values.search.label) {
            debouncedSearchTypingHit({query: values.search.label});
            // Discard a popular-city pending so a subsequent submit doesn't fire popular_search_click.
            clearPendingAmplitudeSearch();
        }

        formActions.update(values);
    };
    const submitWithPending = (fv: SearchFormValuesType, userQueryParams?: {distance?: number}) => {
        // Fire global_search staged by a popular city click before navigating; no-op when
        // the submit was triggered by a filter change or a manually typed region.
        firePendingAmplitudeSearch();
        onSubmit(fv, userQueryParams);
    };

    const onSearchSubmit = (userQueryParams?: {distance?: number}) => {
        // Fire global_search first so _lastSearchMethod is set before trackAmplitudeSearchAdvanced reads it.
        firePendingAmplitudeSearch();
        trackAmplitudeSearchAdvanced(userQueryParams);
        onSubmit(formValues, userQueryParams);
    };

    const onSearchFiltersSubmit = () => {
        trackAmplitudeListingFiltersApplied({formValues: formValues as ISearchFiltersFormValues, latestQuery});
        onSubmit(formValues);
    };

    const {onAfterChange} = useSearchAndFilterUpdate({
        resetPrivilegedOfferList,
        formValues,
        latestQuery,
        onSubmit: submitWithPending
    });

    const {isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters} = useFilter({
        formValues,
        formActions: searchFormActionsOnly,
        closeMobileFilterModal: closeMobileFilterModal,
        onAfterChange: onAfterChange,
        onSubmit: (formValues: SearchFormValuesType, userQueryParams?: {distance?: number}) => onSubmit(formValues, userQueryParams),
        enableFiltersAutoOpen: false,
        disableAutoShowcase: disableFilterAutoShowcase
    });

    const filtersButtonCLick = () => {
        dispatch(filtersModalOpened({filtersModalOpened: true}));
        setDrawerOpen(true);
    };

    /**
     * Handle filters
     */
    const searchFiltersConfig = getSearchFiltersByOfferType(formValues.type?.[0]);
    const {totalCount: numberOfSearchFilters} = useCountSearchFilters(formValues as SearchFormValuesType, searchFiltersConfig);

    const {listUrl, mapUrl} = useOffersViewsUrl();

    const onTravelTimeCancel = () => {
        regionSearchTabChangeHandler(RegionSearchTabsType.LOCATION);
        dispatch(resetPlaceList());
    };

    const renderSearchAutocomplete = (onDropdownStatusChange: (isOpen: boolean) => void, isInModal?: boolean) => {
        if (disableLocationSearch) {
            return null;
        }

        return isAbroadOffersView ? (
            <SearchAutocompleteWithDropdown
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={setLocationSearchOpenStatus}
                formProps={{
                    formValues,
                    formActions,
                    fetchAllSearchLists,
                    latestQuery
                }}
                onAfterChange={onAfterChange}
                onSearchSubmit={onSearchSubmit}
                onAfterTabSwitch={() => null}
                onInputMount={() => null}
                includeDistanceFilter
                isInModal={isInModal}
                placeholder="Zmień lokalizację"
                showArrowOnMobile
                blockBodyScroll
            />
        ) : (
            <SearchAutocompleteWithRegionAdjustments
                ref={searchAutocompleteWrapperRef}
                activeRegionSearchTab={activeRegionSearchTab}
                defaultDistanceValue={defaultDistanceValue}
                fetchAllSearchLists={fetchAllSearchLists}
                formActions={searchFormActionsOnly}
                formValues={formValues as SearchFormValuesType}
                isInModal={isInModal}
                onAfterChange={onAfterChange}
                onAfterTabSwitch={onAfterTabSwitch}
                onChange={onChange}
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={onDropdownStatusChange}
                onInputMount={onInputMount}
                onSearchSubmit={onSearchSubmit}
                onTravelTimeCancel={onTravelTimeCancel}
                setDesktopTypeBlendActive={setDesktopTypeBlendActive}
                slotOrder={slotOrder}
                regionSearchTabChangeHandler={regionSearchTabChangeHandler}
                regionSearchTabs={regionSearchTabs}
                blockBodyScroll
            />
        );
    };

    /**
     * Render
     */
    const defaultDistanceValue = useDefaultDistance(formValues.distance || latestQuery?.distance);
    const mobileFiltersProps = {
        selectedRegionsCount: formValues["search"].regions ? formValues["search"].regions.length : 0,
        selectedFiltersCount: numberOfSearchFilters,
        onToggleSearchClick: toggleLocationSearch,
        onToggleFiltersClick: toggleMobileFilterModal,
        onToggleSortClick: sortActive ? () => setMobileSortActive((prev) => !prev) : null,
        onSemanticSearchClick: () => {
            hitAiAssistant("open");
            setMobileSemanticSearchModalOpen(true);
        },
        mapUrl: shouldRenderListBackBtn ? mapUrl : undefined,
        listUrl: !shouldRenderListBackBtn ? listUrl : undefined,
        latestQuery,
        onMount: onMobileSearchTriggerMount,
        firstSelectedRegion: formValues.search?.regions?.[0]?.name,
        sortActive
    };

    return (
        <div className={cx(wrapperStyle, className)}>
            <DesktopBlend
                isActive={isDesktopSearchBlendActive || isDesktopTypeBlendActive}
                pageHeight={`${windowHeight}px`}
                top={blendTop || `${searchFormWithDrawerHeight}px`}
                heightDecrease={blendHeightDecrease ? blendHeightDecrease : `${searchFormWithDrawerHeight}px`}
            />

            <Hidden on="desktop" className={css({py: 1.5})}>
                <HotlineInfo hotlinePhoneNumber={HOTLINE_INFO_PHONE_NUMBER} />
            </Hidden>

            {!isMobile && (
                <div className={searchSectionWrapper}>
                    <Hidden on="mobile">
                        <HotlineInfo hotlinePhoneNumber={HOTLINE_INFO_PHONE_NUMBER} />
                    </Hidden>

                    <div className={searchFormWrapperStyle}>
                        <div className={searchInputWrapper}>
                            <div id={googlePlacesDivId} />
                            {renderSearchAutocomplete(setLocationSearchOpenStatus)}
                        </div>
                    </div>
                    <div className={filtersButtonsStyle}>
                        <Button size="small" variant="filled_primary" iconLeft={FiltersIcon} className={actionButtonStyle} onClick={filtersButtonCLick}>
                            FILTRY
                            {numberOfSearchFilters > 0 ? (
                                <Badge variant="label_danger" className={filtersCountBadge}>
                                    {numberOfSearchFilters}
                                </Badge>
                            ) : null}
                        </Button>
                        {formValues.search?.regions && (
                            <NewsletterFlowWithRegistrationTrigger variant={NotificationVariant.ICON} amplitudePlacement="rp_save_alert_search_drawer" />
                        )}
                    </div>
                </div>
            )}

            <SearchFilters
                useFilterHook={{isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters}}
                useMobileFilterHook={{isMobileFiltersActive, closeMobileFilterModal, prevFilterState, toggleMobileFilterModal}}
                latestQuery={latestQuery}
                handleSubmit={onSearchSubmit}
                values={formValues as SearchFormValuesType}
                onChange={formActions.update}
                onAfterChange={onAfterChange}
                onSubmit={onSearchFiltersSubmit}
                isAbroad={isAbroadOffersView}
            />

            {isMobile && (
                <>
                    {showTopFilters ? (
                        <MobileTopSearchTriggers
                            forceShowTopSearchTrigger={showMobileTopSearchTrigger}
                            searchFieldPropsValue={formValues.search}
                            {...mobileFiltersProps}
                            isMap={isMap}
                            showLocationSearch
                        />
                    ) : (
                        <MobileSearchTriggers {...mobileFiltersProps} positionFixed hideBottomPadding={hideBottomPadding} />
                    )}
                    {sortActive && (
                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={() => setMobileSortActive(false)}>
                            <OfferListSortMobile
                                name="mobile-sort"
                                onSubmit={() => setMobileSortActive(false)}
                                latestQuerySort={latestQuery.sort as SearchSortString}
                                onMobileResetForm={resetFilters}
                            />
                        </MobileOptionsModal>
                    )}

                    <MobileOptionsModal
                        modalHeading={
                            <span className={css({display: "inline-flex", alignItems: "center", gap: 1})}>
                                Asystent AI
                                <SemanticSearchBetaBadge />
                            </span>
                        }
                        isOpen={isMobileSemanticSearchModalOpen}
                        onModalClose={() => {
                            hitAiAssistant("close");
                            setMobileSemanticSearchModalOpen(false);
                        }}
                        className={css({justifyContent: !hasConversation ? "center" : "initial"})}
                        scrollContainerRef={mobileSemanticSearchScrollRef}
                    >
                        {!hasConversation && <SemanticSearchClientIcon />}
                        <SemanticSearchSidebarHeader className={sidebarHeader} />
                        <SemanticSearchConversation containerRef={mobileSemanticSearchScrollRef} />
                        <SemanticSearchQuery hasStickySidebar />
                    </MobileOptionsModal>

                    <MobileOptionsModal
                        modalHeading="Wyszukiwanie"
                        isOpen={isLocationSearchOpen}
                        onModalClose={() => {
                            setLocationSearchOpenStatus(false);
                        }}
                    >
                        <div id={googlePlacesDivId} />
                        {renderSearchAutocomplete(setLocationSearchOpenStatus, true)}
                    </MobileOptionsModal>
                </>
            )}
        </div>
    );
};

const searchSectionWrapper = css({
    display: "none",

    md: {
        mx: "auto",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        pt: 2,
        pb: 1,
        gap: 2,
        position: "relative",
        flexGrow: 1,
        flexShrink: 1,
        flexWrap: "wrap"
    }
});

const searchFormWrapperStyle = css({
    display: "flex",
    flex: "1 1"
});

const searchInputWrapper = css({
    display: "none",

    md: {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        flex: "1 1"
    }
});

const actionButtonStyle = css({
    width: "18rem",
    overflow: "visible",
    display: "none",

    md: {
        display: "inline-flex"
    }
});

const filtersCountBadge = css({
    position: "absolute",
    right: "-0.5rem",
    top: "-0.5rem"
});

const wrapperStyle = css({
    "&.isInNavigationBar": {
        pb: 0,
        "& > div:nth-of-type(2)": {
            pt: 0
        }
    }
});

const filtersButtonsStyle = css({
    display: "flex",
    gap: 2
});

const sidebarHeader = css({
    p: 1.5
});
